import { Decimal } from 'decimal.js';

import { Currency, CURRENCIES } from './constants';

/**
 * Currency utilities for admin interfaces
 */

/**
 * Helper to convert various number types to Decimal
 */
const toDecimal = (amount: number | string | Decimal): Decimal => {
  if (typeof amount === 'string') {
    return new Decimal(amount);
  }
  if (amount instanceof Decimal) {
    return amount;
  }
  return new Decimal(amount);
};

/**
 * Currency configuration
 */
export const CURRENCY_CONFIG = {
  [CURRENCIES.USD]: {
    symbol: '$',
    name: 'US Dollar',
    decimals: 2,
    locale: 'en-US',
  },
  [CURRENCIES.EUR]: {
    symbol: '€',
    name: 'Euro',
    decimals: 2,
    locale: 'de-DE',
  },
  [CURRENCIES.GBP]: {
    symbol: '£',
    name: 'British Pound',
    decimals: 2,
    locale: 'en-GB',
  },
  [CURRENCIES.CAD]: {
    symbol: 'C$',
    name: 'Canadian Dollar',
    decimals: 2,
    locale: 'en-CA',
  },
  [CURRENCIES.AUD]: {
    symbol: 'A$',
    name: 'Australian Dollar',
    decimals: 2,
    locale: 'en-AU',
  },
} as const;

/**
 * Get currency configuration
 */
export const getCurrencyConfig = (currency: Currency) => {
  return CURRENCY_CONFIG[currency];
};

/**
 * Format amount with currency symbol
 */
export const formatCurrencyAmount = (
  amount: number | string | Decimal,
  currency = CURRENCIES.USD,
  options: {
    showSymbol?: boolean;
    showCode?: boolean;
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string => {
  const config = getCurrencyConfig(currency);
  const {
    showSymbol = true,
    showCode = false,
    locale = config.locale,
    minimumFractionDigits = config.decimals,
    maximumFractionDigits = config.decimals,
  } = options;

  let numericAmount: number;
  if (typeof amount === 'string') {
    numericAmount = parseFloat(amount);
  } else if (amount instanceof Decimal) {
    numericAmount = amount.toNumber();
  } else {
    numericAmount = amount;
  }

  if (Number.isNaN(numericAmount)) return '—';

  const formatter = new Intl.NumberFormat(locale, {
    style: showSymbol ? 'currency' : 'decimal',
    currency: showSymbol ? currency : undefined,
    minimumFractionDigits,
    maximumFractionDigits,
  });

  const formatted = formatter.format(numericAmount);

  if (showCode && !showSymbol) {
    return `${formatted} ${currency}`;
  }

  return formatted;
};

/**
 * Parse currency string to decimal
 */
export const parseCurrencyAmount = (
  currencyString: string,
  currency = CURRENCIES.USD
): Decimal | null => {
  try {
    const config = getCurrencyConfig(currency);

    // Remove currency symbols, spaces, and non-numeric characters except decimal point and minus
    const cleaned = currencyString
      .replace(new RegExp(`[${config.symbol}\\s,]`, 'g'), '')
      .replace(/[^\d.-]/g, '');

    if (!cleaned || cleaned === '-') return null;

    return new Decimal(cleaned);
  } catch (error) {
    return null;
  }
};

/**
 * Convert between currencies (requires exchange rates)
 */
export const convertCurrency = (
  amount: number | string | Decimal,
  fromCurrency: Currency,
  toCurrency: Currency,
  exchangeRate: number
): Decimal => {
  const numericAmount = toDecimal(amount);

  if (fromCurrency === toCurrency) {
    return numericAmount;
  }

  return numericAmount.mul(exchangeRate);
};

/**
 * Calculate tax amount
 */
export const calculateTax = (
  amount: number | string | Decimal,
  taxRate: number
): Decimal => {
  const numericAmount = toDecimal(amount);

  return numericAmount.mul(taxRate).div(100);
};

/**
 * Calculate total with tax
 */
export const calculateTotalWithTax = (
  amount: number | string | Decimal,
  taxRate: number
): Decimal => {
  const numericAmount = toDecimal(amount);

  const tax = calculateTax(numericAmount, taxRate);
  return numericAmount.add(tax);
};

/**
 * Calculate discount amount
 */
export const calculateDiscount = (
  amount: number | string | Decimal,
  discountPercent: number
): Decimal => {
  const numericAmount = toDecimal(amount);

  return numericAmount.mul(discountPercent).div(100);
};

/**
 * Calculate total with discount
 */
export const calculateTotalWithDiscount = (
  amount: number | string | Decimal,
  discountPercent: number
): Decimal => {
  const numericAmount = toDecimal(amount);

  const discount = calculateDiscount(numericAmount, discountPercent);
  return numericAmount.sub(discount);
};

/**
 * Round currency amount to proper decimals
 */
export const roundCurrencyAmount = (
  amount: number | string | Decimal,
  currency = CURRENCIES.USD
): Decimal => {
  const config = getCurrencyConfig(currency);
  const numericAmount = toDecimal(amount);

  return numericAmount.toDecimalPlaces(config.decimals, Decimal.ROUND_HALF_UP);
};

/**
 * Check if amount is valid currency value
 */
export const isValidCurrencyAmount = (
  amount: string,
  currency = CURRENCIES.USD
): boolean => {
  const parsed = parseCurrencyAmount(amount, currency);
  return parsed !== null && parsed.isFinite() && parsed.gte(0);
};

/**
 * Get available currencies as options
 */
export const getCurrencyOptions = () => {
  return Object.entries(CURRENCY_CONFIG).map(([code, config]) => ({
    value: code,
    label: `${config.name} (${config.symbol})`,
    symbol: config.symbol,
    name: config.name,
  }));
};

/**
 * ISO-4217 minor-unit decimals
 * ----------------------------
 * The number of fractional digits a currency's minor unit uses. This is the
 * single source of truth for converting a human-entered major-unit amount to
 * the integer minor-unit amount the backend stores (e.g. EUR "12.34" → 1234,
 * JPY "1000" → 1000, BHD "1.234" → 1234). The backend performs no per-currency
 * validation and interprets the stored integer via `Money.fromSubUnits` at
 * order time, so this map is the only guard against writing an amount at the
 * wrong scale.
 *
 * Kept as an explicit list because the project targets ES2020 and cannot use
 * `Intl.supportedValuesOf('currency')` / `Intl.NumberFormat().resolvedOptions`.
 */
const ISO_4217_CODES = [
  'AED',
  'AFN',
  'ALL',
  'AMD',
  'ANG',
  'AOA',
  'ARS',
  'AUD',
  'AWG',
  'AZN',
  'BAM',
  'BBD',
  'BDT',
  'BGN',
  'BHD',
  'BIF',
  'BMD',
  'BND',
  'BOB',
  'BRL',
  'BSD',
  'BTN',
  'BWP',
  'BYN',
  'BZD',
  'CAD',
  'CDF',
  'CHF',
  'CLP',
  'CNY',
  'COP',
  'CRC',
  'CUP',
  'CVE',
  'CZK',
  'DJF',
  'DKK',
  'DOP',
  'DZD',
  'EGP',
  'ERN',
  'ETB',
  'EUR',
  'FJD',
  'FKP',
  'GBP',
  'GEL',
  'GHS',
  'GIP',
  'GMD',
  'GNF',
  'GTQ',
  'GYD',
  'HKD',
  'HNL',
  'HTG',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'IQD',
  'IRR',
  'ISK',
  'JMD',
  'JOD',
  'JPY',
  'KES',
  'KGS',
  'KHR',
  'KMF',
  'KPW',
  'KRW',
  'KWD',
  'KYD',
  'KZT',
  'LAK',
  'LBP',
  'LKR',
  'LRD',
  'LSL',
  'LYD',
  'MAD',
  'MDL',
  'MGA',
  'MKD',
  'MMK',
  'MNT',
  'MOP',
  'MRU',
  'MUR',
  'MVR',
  'MWK',
  'MXN',
  'MYR',
  'MZN',
  'NAD',
  'NGN',
  'NIO',
  'NOK',
  'NPR',
  'NZD',
  'OMR',
  'PAB',
  'PEN',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'PYG',
  'QAR',
  'RON',
  'RSD',
  'RUB',
  'RWF',
  'SAR',
  'SBD',
  'SCR',
  'SDG',
  'SEK',
  'SGD',
  'SHP',
  'SLE',
  'SOS',
  'SRD',
  'SSP',
  'STN',
  'SVC',
  'SYP',
  'SZL',
  'THB',
  'TJS',
  'TMT',
  'TND',
  'TOP',
  'TRY',
  'TTD',
  'TWD',
  'TZS',
  'UAH',
  'UGX',
  'USD',
  'UYU',
  'UZS',
  'VES',
  'VND',
  'VUV',
  'WST',
  'XAF',
  'XCD',
  'XOF',
  'XPF',
  'YER',
  'ZAR',
  'ZMW',
  'ZWG',
  'ZWL',
];

const VALID_CURRENCY_CODES = new Set(ISO_4217_CODES);

/** Minor-unit exponent exceptions; every other code uses 2 decimal places. */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'ISK',
  'JPY',
  'KMF',
  'KRW',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);
const THREE_DECIMAL_CURRENCIES = new Set([
  'BHD',
  'IQD',
  'JOD',
  'KWD',
  'LYD',
  'OMR',
  'TND',
]);

/** Whether `code` is an active ISO-4217 currency code. */
export const isValidCurrencyCode = (code: string): boolean =>
  VALID_CURRENCY_CODES.has(code);

/**
 * Decimal places of the currency's minor unit — drives major↔minor-unit
 * conversion. Returns 2 for any unknown or unrecognised code.
 */
export const getCurrencyDecimalPlaces = (code: string): number => {
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3;
  return 2;
};

export interface CurrencyOption {
  /** The ISO 4217 code, which is what the backend stores. */
  value: string;
  /** `EUR — Euro`, so the code stays visible and the name makes it findable. */
  label: string;
}

/**
 * The currencies a distributor can be set to, for a picker.
 *
 * Every ISO 4217 code, not just the five in `CURRENCY_CONFIG`. The backend
 * validates against `currency-codes`' full list
 * (`supplierService/.../schema/supplier.ts`: `Joi.string().case('upper').allow(...cc.codes())`),
 * so narrowing to five would take away currencies CS can set today. Nothing in the
 * distributor form formats this value, so an unconfigured code is only a string
 * passing through.
 *
 * The options are `ISO_4217_CODES` — the list this module already validates
 * against — unioned with whatever the runtime reports. The bundled list is what
 * makes the picker the same everywhere: reading only `Intl.supportedValuesOf` left
 * a runtime that lacks it with five options, which would have taken CHF away from
 * the person editing a Swiss distributor. The union direction matters too, so a
 * newer runtime knowing a code this list predates does not lose it either.
 *
 * `CURRENCY_CONFIG`'s five come first, because they are the common ones and the
 * only ones the app has symbols and decimal counts for. The rest follow
 * alphabetically.
 *
 * Names come from `Intl.DisplayNames`, so there is no list of names to maintain. A
 * runtime that cannot name a code shows the code rather than dropping it.
 */
export const buildCurrencyOptions = (): CurrencyOption[] => {
  const named = (() => {
    try {
      const display = new Intl.DisplayNames(['en'], { type: 'currency' });
      return (code: string) => display.of(code) ?? code;
    } catch {
      return (code: string) => code;
    }
  })();

  const fromRuntime = (() => {
    try {
      // Declared in `vite-env.d.ts`: ES2023, newer than this project's `lib`. The
      // guard is not decoration — an older runtime does not have it.
      return Intl.supportedValuesOf('currency');
    } catch {
      return [];
    }
  })();

  const common = Object.keys(CURRENCY_CONFIG);
  const rest = [...new Set([...ISO_4217_CODES, ...fromRuntime])]
    .filter((code) => !common.includes(code))
    .sort((a, b) => a.localeCompare(b));

  return [...common, ...rest].map((code) => ({
    value: code,
    label: `${code} — ${named(code)}`,
  }));
};

/**
 * The symbol to show beside an amount in `code`, e.g. `₺` for TRY. Uses the
 * configured symbol when there is one, otherwise the runtime's narrow symbol.
 * Returns undefined when neither knows the code.
 */
export const getCurrencySymbol = (code: string): string | undefined => {
  const configured = Object.entries(CURRENCY_CONFIG).find(
    ([configuredCode]) => configuredCode === code
  );
  if (configured) return configured[1].symbol;

  try {
    const symbol = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value;
    // A runtime with no symbol for the code echoes the code back.
    return symbol && symbol !== code ? symbol : undefined;
  } catch {
    return undefined;
  }
};
